import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenantApiToken } from '../../contexts/TenantProfileContext';
import { sendPortalSessionHeartbeat } from '../../services/tenantApi';

const HEARTBEAT_MS = 10_000;
const ACTIVE_WITHIN_MS = 45_000;
const SESSION_STORAGE_KEY = 'tenant-portal-session-id';

/**
 * Tells the server this tab is open. A beat goes out every 10 seconds while
 * the tab is visible. Active time counts only after recent use of the page.
 */
export default function PortalSessionHeartbeat() {
  const getToken = useTenantApiToken();
  const location = useLocation();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const sessionIdRef = useRef<string | null>(null);
  const lastInputRef = useRef(Date.now());

  useEffect(() => {
    const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) sessionIdRef.current = stored;
  }, []);

  useEffect(() => {
    const mark = () => {
      lastInputRef.current = Date.now();
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
    for (const eventName of events) {
      window.addEventListener(eventName, mark, { passive: true });
    }
    return () => {
      for (const eventName of events) window.removeEventListener(eventName, mark);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const beat = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      try {
        const result = await sendPortalSessionHeartbeat(getTokenRef.current, {
          sessionId: sessionIdRef.current,
          path: pathRef.current,
          active: Date.now() - lastInputRef.current < ACTIVE_WITHIN_MS,
        });
        if (cancelled || !result.sessionId) return;
        sessionIdRef.current = result.sessionId;
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, result.sessionId);
      } catch {
        // The next beat tries again.
      }
    };

    void beat();
    const timer = window.setInterval(() => {
      void beat();
    }, HEARTBEAT_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void beat();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return null;
}
